import { useState, useEffect, useRef, useCallback, type CSSProperties } from 'react';
import { createPortal } from 'react-dom';
import { useNavigate } from 'react-router-dom';
import { Bell } from 'lucide-react';
import { useAuth } from '../context/AuthContext';
import notificationsService, { type Notification } from '../services/notifications';
import { useWebNotifications } from '../hooks/useWebNotifications';
import { inductionNotificationPath } from '../utils/inductionNotifications';
import { tilNotificationPath } from '../utils/tilNotifications';

function timeAgo(dateStr: string): string {
  const diff = Date.now() - new Date(dateStr).getTime();
  const mins = Math.floor(diff / 60000);
  if (mins < 1) return 'Just now';
  if (mins < 60) return `${mins}m ago`;
  const hours = Math.floor(mins / 60);
  if (hours < 24) return `${hours}h ago`;
  const days = Math.floor(hours / 24);
  return `${days}d ago`;
}

function getNavigatePath(type: string, data?: Record<string, string>): string {
  if (type === 'timesheet_submitted' || type === 'leave_submitted' || type === 'missed_clock_declared' || type === 'timesheet_approval_reminder') return '/approvals';
  if (type === 'timesheet_approved' || type === 'timesheet_rejected') return '/timesheet/history';
  if (type === 'timesheet_submit_nudge' || type === 'timesheet_reminder') return '/timesheet';
  if (type === 'leave_approved' || type === 'leave_rejected') return '/timesheet';
  const induction = inductionNotificationPath(type, data);
  if (induction) return induction;
  const til = tilNotificationPath(type, data);
  if (til) return til;
  // An insurance policy due for renewal or lapsed: the policy, which names
  // every machine on it. Ahead of the machine rule — a one-machine policy
  // carries an equipment_id too.
  if (type.startsWith('equipment_insurance_') && data?.policy_id) return `/equipment/insurance/${data.policy_id}`;
  // Service due / overdue and loans past their return date.
  if (type.startsWith('equipment_') && data?.equipment_id) return `/equipment/${data.equipment_id}`;
  // A payment receipt was attached to a purchase order: accounts opens the order.
  if (type === 'po_receipt_added' && data?.po_id) return `/purchase-orders/${data.po_id}`;
  // A credit note asked for, or decided: the requests page, on that request.
  if (type.startsWith('credit_note_request') && data?.request_id) return `/accounts/credit-note-requests?request=${data.request_id}`;
  // Somebody needs propagation material nobody has: where to get it is asked for.
  if (type === 'nursery_sourcing_request') return '/nursery/material?tab=requests';
  // A spray's weather went outside (or back inside) the limits set on it.
  if (type.startsWith('spray_weather_') && data?.spray_id) return `/spray/${data.spray_id}`;
  // Rain skipped watering at a site: its live map says which valves and why.
  if (type === 'irrigation_rain_skip' && data?.controller_id) return `/irrigation/espnow/${data.controller_id}?tab=map`;
  if (type === 'pest_alert') {
    const q = new URLSearchParams();
    if (data?.farm_id) q.set('farm', data.farm_id);
    if (data?.pest_model_id) q.set('pest', data.pest_model_id);
    const qs = q.toString();
    return `/weather/pests${qs ? `?${qs}` : ''}`;
  }
  return '/dashboard';
}

function NotificationBell() {
  const { isAuthenticated, isLoading } = useAuth();
  const navigate = useNavigate();
  const [unreadCount, setUnreadCount] = useState(0);
  const [notifications, setNotifications] = useState<Notification[]>([]);
  const [open, setOpen] = useState(false);
  const [dropdownStyle, setDropdownStyle] = useState<CSSProperties>({});
  const dropdownRef = useRef<HTMLDivElement>(null);
  const buttonRef = useRef<HTMLButtonElement>(null);
  const prevUnreadRef = useRef(0);
  const shownIdsRef = useRef(new Set<number>());
  const initializedRef = useRef(false);
  const { showNotification, requestPermission, permission } = useWebNotifications();

  // Only fetch once auth is fully resolved and the user is authenticated
  const ready = isAuthenticated && !isLoading;

  const fetchCount = useCallback(async () => {
    if (!ready) return;
    try {
      const res = await notificationsService.getUnreadCount();
      if (res.success && res.data) {
        const newCount = res.data.count;
        setUnreadCount(newCount);

        // On the very first fetch, silently record existing unread IDs so we
        // don't re-fire desktop notifications for items the user already saw.
        if (!initializedRef.current) {
          initializedRef.current = true;
          prevUnreadRef.current = newCount;
          if (newCount > 0) {
            const listRes = await notificationsService.getNotifications();
            if (listRes.success && listRes.data) {
              listRes.data.filter(n => !n.read).forEach(n => shownIdsRef.current.add(n.id));
            }
          }
          return;
        }

        const prev = prevUnreadRef.current;
        prevUnreadRef.current = newCount;

        // Show desktop notifications for newly arrived items on web
        if (newCount > prev) {
          const listRes = await notificationsService.getNotifications();
          if (listRes.success && listRes.data) {
            const newItems = listRes.data.filter(
              n => !n.read && !shownIdsRef.current.has(n.id)
            );
            newItems.forEach(n => {
              shownIdsRef.current.add(n.id);
              showNotification(n.title, n.body ?? undefined, () => {
                navigate(getNavigatePath(n.type, n.data));
              });
            });
          }
        }
      }
    } catch {
      // Silently fail — notification badge is non-critical
    }
  }, [ready, showNotification, navigate]);

  // Poll every 60 seconds — only starts once auth is ready
  useEffect(() => {
    if (!ready) return;
    fetchCount();
    const interval = setInterval(fetchCount, 60000);
    return () => clearInterval(interval);
  }, [ready, fetchCount]);

  // Refresh badge immediately when a foreground push is received on native
  useEffect(() => {
    if (!ready) return;
    window.addEventListener('push-notification-received', fetchCount);
    return () => window.removeEventListener('push-notification-received', fetchCount);
  }, [ready, fetchCount]);

  // Close dropdown when clicking outside (works with portalled dropdown)
  useEffect(() => {
    function handleClickOutside(e: MouseEvent) {
      const target = e.target as Node;
      const insideButton = buttonRef.current?.contains(target);
      const insideDropdown = dropdownRef.current?.contains(target);
      if (!insideButton && !insideDropdown) {
        setOpen(false);
      }
    }
    if (open) {
      document.addEventListener('mousedown', handleClickOutside);
    }
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [open]);

  // Compute fixed position so the dropdown is always within the viewport
  useEffect(() => {
    if (open && buttonRef.current) {
      const rect = buttonRef.current.getBoundingClientRect();
      const dropdownWidth = Math.min(320, window.innerWidth - 16);
      const top = rect.bottom + 8;
      // Align right edge with button, but clamp so left edge is always ≥ 8px
      const idealRight = window.innerWidth - rect.right;
      const right = Math.max(8, Math.min(idealRight, window.innerWidth - dropdownWidth - 8));
      setDropdownStyle({ position: 'fixed', top, right, width: dropdownWidth, zIndex: 50 });
    }
  }, [open]);

  async function handleOpen() {
    if (open) {
      setOpen(false);
      return;
    }
    // Request desktop notification permission on first bell click (requires user gesture)
    if (permission === 'default') {
      requestPermission();
    }
    setOpen(true);
    try {
      const res = await notificationsService.getNotifications();
      if (res.success && res.data) {
        setNotifications(res.data);
      }
      // Mark all as read once the panel opens
      if (unreadCount > 0) {
        await notificationsService.markAllRead();
        setUnreadCount(0);
        setNotifications(prev => prev.map(n => ({ ...n, read: true })));
      }
    } catch {
      // Non-critical
    }
  }

  function handleNotificationClick(n: Notification) {
    setOpen(false);
    navigate(getNavigatePath(n.type, n.data));
  }

  if (!ready) return null;

  return (
    <>
      <button
        ref={buttonRef}
        onClick={handleOpen}
        className="relative flex items-center justify-center h-9 w-9 rounded-lg border border-neutral-200 bg-white hover:bg-neutral-50 transition-colors"
        aria-label="Notifications"
      >
        <Bell className="h-4 w-4 text-neutral-600" />
        {unreadCount > 0 && (
          <span className="absolute -top-1 -right-1 flex h-4 w-4 items-center justify-center rounded-full bg-red-500 text-[10px] font-bold text-white leading-none">
            {unreadCount > 9 ? '9+' : unreadCount}
          </span>
        )}
      </button>

      {open && createPortal(
        <div ref={dropdownRef} className="rounded-xl border border-neutral-200 bg-white shadow-lg" style={dropdownStyle}>
          <div className="flex items-center justify-between px-4 py-3 border-b border-neutral-100">
            <span className="text-sm font-semibold text-neutral-900">Notifications</span>
            {notifications.some(n => !n.read) && (
              <button
                onClick={async () => {
                  await notificationsService.markAllRead();
                  setUnreadCount(0);
                  setNotifications(prev => prev.map(n => ({ ...n, read: true })));
                }}
                className="text-xs text-primary-600 hover:underline"
              >
                Mark all read
              </button>
            )}
          </div>

          <div className="max-h-80 overflow-y-auto divide-y divide-neutral-50">
            {notifications.length === 0 ? (
              <p className="px-4 py-6 text-center text-sm text-neutral-500">No notifications yet</p>
            ) : (
              notifications.map(n => (
                <button
                  key={n.id}
                  onClick={() => handleNotificationClick(n)}
                  className={`w-full text-left px-4 py-3 hover:bg-neutral-50 transition-colors ${
                    !n.read ? 'bg-blue-50/50' : ''
                  }`}
                >
                  <div className="flex items-start gap-2">
                    {!n.read && (
                      <span className="mt-1.5 h-2 w-2 flex-shrink-0 rounded-full bg-blue-500" />
                    )}
                    <div className={!n.read ? '' : 'pl-4'}>
                      <p className="text-sm font-medium text-neutral-900">{n.title}</p>
                      {n.body && (
                        <p className="text-xs text-neutral-600 mt-0.5 line-clamp-2">{n.body}</p>
                      )}
                      <p className="text-xs text-neutral-400 mt-1">{timeAgo(n.created_at)}</p>
                    </div>
                  </div>
                </button>
              ))
            )}
          </div>
        </div>,
        document.body
      )}
    </>
  );
}

export default NotificationBell;
